class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._scrollLocked = false;
    this._isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  lockPageScroll() {
    if (this._scrollLocked) return;

    this.scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this._scrollLocked = true;

    document.documentElement.classList.add('cart-drawer-scroll-lock');
    document.body.classList.add('cart-drawer-scroll-lock');
    document.documentElement.style.setProperty('overflow', 'hidden');
    document.body.style.setProperty('overflow', 'hidden');
    document.body.style.setProperty('position', 'fixed');
    document.body.style.setProperty('top', `-${this.scrollPosition}px`);
    document.body.style.setProperty('left', '0');
    document.body.style.setProperty('right', '0');
    document.body.style.setProperty('width', '100%');
  }

  unlockPageScroll() {
    if (!this._scrollLocked) return;

    const scrollY = this.scrollPosition || 0;
    this._scrollLocked = false;

    document.documentElement.classList.remove('cart-drawer-scroll-lock');
    document.body.classList.remove('cart-drawer-scroll-lock');
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    document.body.style.removeProperty('width');

    window.scrollTo(0, scrollY);
    document.documentElement.scrollTop = scrollY;
    document.body.scrollTop = scrollY;
  }

  restorePageScroll() {
    if (typeof this.scrollPosition !== 'number') return;
    window.scrollTo(0, this.scrollPosition);
    document.documentElement.scrollTop = this.scrollPosition;
    document.body.scrollTop = this.scrollPosition;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.lockPageScroll();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (!this._isIOS) {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        }
        // Do NOT call restorePageScroll() while body is position:fixed —
        // that fights the lock and blanks the page behind the overlay.
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockPageScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    const skipIconUpdate = options.skipIconUpdate === true;

    // Update drawer HTML first — do not lock scroll or show overlay until content is ready
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    const sectionHtml = parsedState.sections?.['cart-drawer'];
    if (!sectionHtml) {
      // Item may already be in cart; recover by fetching the drawer section instead of silent fail.
      console.error('Cart drawer section HTML missing from add-to-cart response — recovering');
      this._recoverDrawerFromCart(parsedState, options);
      return;
    }

    this._applyDrawerSections(parsedState, skipIconUpdate);
  }

  _applyDrawerSections(parsedState, skipIconUpdate) {
    this.getSectionsToRender().forEach((section) => {
      if (skipIconUpdate && section.id === 'cart-icon-bubble') return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector);
      if (html === null) return;
      sectionElement.innerHTML = html;
    });

    this.classList.toggle('is-empty', parsedState.item_count === 0);

    // Rebind overlay after #CartDrawer.innerHTML replace, then open once content exists
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      if (!this._boundClose) this._boundClose = this.close.bind(this);
      overlay.removeEventListener('click', this._boundClose);
      overlay.addEventListener('click', this._boundClose);
    }

    this.open();
  }

  _recoverDrawerFromCart(parsedState, options = {}) {
    const skipIconUpdate = options.skipIconUpdate === true;
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const recovered = {
          ...parsedState,
          sections: {
            ...(parsedState.sections || {}),
            'cart-drawer': responseText,
          },
        };
        this._applyDrawerSections(recovered, skipIconUpdate);
      })
      .catch((e) => {
        console.error(e);
        // Last resort: keep purchase path alive
        if (window.routes && window.routes.cart_url) {
          window.location = window.routes.cart_url;
        }
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // Only re-render the drawer panel. Trik header cart badge is synced via cartUpdate pub/sub
    // (trik-truk.js). Updating #cart-icon-bubble with Dawn section HTML can replace Trik markups
    // and, when the selector miss-matches, throw mid-update and leave the drawer in a broken state.
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
